// Danger zone — self-service account deletion (F5).

import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { api, ApiError } from "@/lib/api";
import { clearTokens } from "@/lib/tokens";
import { useAuthStore } from "@/stores/authStore";
import { useTwoFactorStatus } from "@/hooks/useTwoFactor";
import { StepUpProvider, useStepUp } from "@/context/StepUpContext";
import { useDict } from "@/i18n/LocaleProvider";

function formatErr(err: unknown, soleAdminTmpl?: string): string {
  if (err instanceof ApiError) {
    if (typeof err.detail === "string") return err.detail;
    if (err.detail && typeof err.detail === "object") {
      const d = err.detail as { message?: string; orgs?: { name: string }[]; reason?: string };
      if (d.reason === "last_org_admin" && d.orgs?.length) {
        const names = d.orgs.map((o) => o.name).join(", ");
        return soleAdminTmpl
          ? soleAdminTmpl.replace("{names}", names)
          : `You're the sole admin of: ${names}. Transfer admin rights or delete those orgs before closing your account.`;
      }
      if (d.message) return d.message;
      return JSON.stringify(err.detail);
    }
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

function DeleteAccountInner() {
  const t = useDict().app.profile.deleteAccount;
  const logout = useAuthStore((s) => s.logout);
  const status = useTwoFactorStatus();
  const { withStepUp } = useStepUp();
  const has2fa = status.data?.enabled ?? false;

  const [confirming, setConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const [typedConfirm, setTypedConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function doDelete(stepUpToken?: string) {
    setBusy(true);
    setErr(null);
    try {
      const headers: Record<string, string> = {};
      if (stepUpToken) headers["X-StepUp"] = stepUpToken;

      await api("/auth/me", {
        method: "DELETE",
        body: { password: has2fa ? undefined : password },
        headers,
      });

      // Clear local session and hard-redirect to the public home. The
      // account no longer exists, so there's nothing to log into; a full
      // reload also avoids racing RequireAuth (this runs from /app/profile).
      clearTokens();
      logout();
      window.location.assign("/");
    } catch (e) {
      setErr(formatErr(e, t.soleAdmin));
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (typedConfirm.trim().toUpperCase() !== "DELETE") {
      setErr(t.typeDeleteErr);
      return;
    }
    if (has2fa) {
      withStepUp(async (token) => {
        await doDelete(token);
      }, t.stepUpReason);
    } else {
      if (!password) {
        setErr(t.passwordRequired);
        return;
      }
      await doDelete();
    }
  }

  if (!confirming) {
    return (
      <Card style={{ border: "1px solid rgb(var(--color-danger) / 0.267)" }}>
        <div className="font-mono text-[11px] text-danger uppercase tracking-[0.08em] mb-2">
          {t.dangerZone}
        </div>
        {/* Title + explainer first, button on its own line below. The
            previous side-by-side layout squashed the explanatory text
            and made the destructive button compete with the description
            for attention. */}
        <div>
          <div className="font-sans text-[13px] text-content font-semibold mb-1">
            {t.deleteTitle}
          </div>
          <div className="font-mono text-[11px] text-muted leading-[1.6] mb-4">
            {t.deleteDesc}
          </div>
          <Button variant="danger" onClick={() => setConfirming(true)}>
            {t.deleteBtn}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-danger" style={{ border: "1px solid rgb(var(--color-danger) / 0.533)" }}>
      <div className="font-mono text-[11px] text-danger uppercase tracking-[0.08em] mb-3">
        {t.confirmTitle}
      </div>
      <div className="font-mono text-[11px] text-muted mb-4 leading-[1.6]">
        {t.confirmPre}<strong className="text-danger">DELETE</strong>
        {" "}{t.confirmMid}{has2fa ? t.with2fa : t.withPwd}.
      </div>

      <Input
        label={t.typeToConfirm}
        value={typedConfirm}
        onChange={setTypedConfirm}
        placeholder={t.deletePlaceholder}
      />

      {!has2fa && (
        <Input
          label={t.currentPassword}
          value={password}
          onChange={setPassword}
          type="password"
          placeholder="••••••••"
        />
      )}

      {err && (
        <div className="px-3 py-2 bg-danger-bg border border-danger/40 rounded text-danger font-mono text-[11px] mb-3">
          {err}
        </div>
      )}

      <div className="flex gap-2 mt-3">
        <Button
          variant="danger"
          onClick={submit}
          disabled={busy || typedConfirm.trim().toUpperCase() !== "DELETE"}
        >
          {busy ? t.deleting : t.permanentlyDelete}
        </Button>
        <Button
          variant="default"
          onClick={() => {
            setConfirming(false);
            setErr(null);
            setPassword("");
            setTypedConfirm("");
          }}
        >
          {t.cancel}
        </Button>
      </div>
    </Card>
  );
}

export function DeleteAccountCard() {
  return (
    <StepUpProvider>
      <DeleteAccountInner />
    </StepUpProvider>
  );
}
